import { TSection } from "@/components/MiniCards/MiniCards";
import { TWRAccount, TWRPortfolio } from "@/services/WealthReaderService";
import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";
import { longDecimalFormatter, setLocaleCurrency, shortDecimalFormatter } from "@/utils/numberFormatter";
import {
    DATA_RECEIVED_PORTFOLIO_LABELS,
    DATA_RECEIVED_SUMMARY_LABELS,
    EMPTY_VALUE,
    PORTFOLIO_SUBTYPE_LABELS
} from "../../Texts/PlanesExternos";

export const formatPercent = (value?: number | null): string => {
    if (value === null || value === undefined) {
        return EMPTY_VALUE;
    }

    return `${shortDecimalFormatter.format(value)} %`;
};

export const formatCurrency = (value?: number | null, currency?: string | null): string => {
    if (value === null || value === undefined) {
        return EMPTY_VALUE;
    }

    return setLocaleCurrency(currency ?? "EUR", value);
};

export const formatShares = (value?: number | null): string => {
    if (value === null || value === undefined) {
        return EMPTY_VALUE;
    }

    return longDecimalFormatter.format(value);
};

export const formatText = (value?: string | null): string => {
    if (value === null || value === undefined || value.trim() === "") {
        return EMPTY_VALUE;
    }

    return value;
};

export const formatDate = (value?: string | null): string => {
    if (!value) {
        return EMPTY_VALUE;
    }

    return formatIsoDateTimeToDdMmYyyy(value);
};


export const formatDocument = (source?: { iD?: string; ID?: string } | null): string => {
    if (!source) {
        return EMPTY_VALUE;
    }

    return formatText(source.iD ?? source.ID);
};

const PORTFOLIO_SUBTYPE_BY_INDEX = ["funds", "stocks", "pension-plans", "alternative-investments"];

export const formatPortfolioSubtype = (subtype?: unknown): string => {
    if (subtype === null || subtype === undefined) {
        return EMPTY_VALUE;
    }

    const key = typeof subtype === "number"
        ? PORTFOLIO_SUBTYPE_BY_INDEX[subtype]
        : String(subtype);

    return PORTFOLIO_SUBTYPE_LABELS[key] ?? formatText(key);
};

export const buildSummarySections = (portfolios: TWRPortfolio[], accounts: TWRAccount[]): TSection[] => {
    const portfoliosValue = portfolios.reduce((total, portfolio) => total + (portfolio.total_value ?? 0), 0);
    const accountsBalance = accounts.reduce((total, account) => total + (account.balances?.current ?? 0), 0);

    return [
        {
            title: DATA_RECEIVED_SUMMARY_LABELS.portfoliosCount,
            data: shortDecimalFormatter.format(portfolios.length)
        },
        {
            title: DATA_RECEIVED_SUMMARY_LABELS.portfoliosValue,
            data: formatCurrency(portfoliosValue, portfolios[0]?.currency)
        },
        {
            title: DATA_RECEIVED_SUMMARY_LABELS.accountsCount,
            data: shortDecimalFormatter.format(accounts.length)
        },
        {
            title: DATA_RECEIVED_SUMMARY_LABELS.accountsBalance,
            data: formatCurrency(accountsBalance, accounts[0]?.currency)
        }
    ];
};

export const buildPortfolioSections = (portfolio: TWRPortfolio): TSection[] =>
    [
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.totalValue,
            data: formatCurrency(portfolio.total_value, portfolio.currency)
        },
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.weightedReturn,
            data: formatPercent(portfolio.weighted_return)
        },
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.annualizedVolatility,
            data: formatPercent(portfolio.annualized_volatility)
        },
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.yield,
            data: formatCurrency(portfolio.yield)
        },
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.contributions,
            data: formatCurrency(portfolio.contributions, portfolio.currency)
        },
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.withholdings,
            data: formatCurrency(portfolio.withholdings, portfolio.currency)
        },
        {
            title: DATA_RECEIVED_PORTFOLIO_LABELS.contractingDate,
            data: formatDate(portfolio.contracting_date)
        }
    ];
